<template>
  <Form
    id="sample-stock"
    v-slot="{ errors, meta, handleReset, values: formValues }"
    ref="myForm"
    class="w-full"
    :validation-schema="schema"
    :initial-dirty="initialDirty"
    @submit="handleForm"
  >
    <div class="w-3/5 mt-3">
      <label class="text-xs uppercase font-semibold" for="stockType">{{
        $t('stockBook.form.category')
      }}</label>
      <Field
        v-slot="{ field }"
        v-model="newStock.stockCategoryId"
        name="stockCategoryId"
      >
        <Multiselect
          id="stockCategoryId"
          v-bind="field"
          v-model="newStock.stockCategoryId"
          mode="single"
          name="stockCategoryId"
          :options="stockCategories"
          :placeholder="$t('stockBook.form.categoryPlaceholder')"
          :groups="false"
          @change="categoryChanged"
        />
      </Field>
    </div>
    <!-- SECTION ALIAS -->
    <section class="w-full mt-3">
      <h3>Alias</h3>
      <div class="stock__aliasPrincipal form-group flex justify-start w-full">
        <div class="flex flex-col w-3/5">
          <label class="text-sm">{{ $t('global.principalAlias') }}</label>
          <Field
            v-slot="{ field }"
            v-model="newStock.alias"
            type="text"
            name="alias"
          >
            <Input
              v-model="newStock.alias"
              placeholder="(ex: AO)"
              v-bind="field"
              name="alias"
              type="text"
              :disabled="false"
              class="h-10"
            />
          </Field>
        </div>
      </div>
    </section>
    <FormActions
      v-if="canDo"
      :errors="errors"
      :meta="meta"
      @reset-form="handleReset"
      @onSubmit="handleForm(formValues)"
    />
  </Form>
</template>

<script lang="ts">
  import { computed, defineComponent, onMounted, ref, watch } from 'vue'
  import { Form, Field } from 'vee-validate'
  import { Input } from '@up.law/uplaw-ui'
  import { SampleStockTypeFormData, STOCKTYPESALIAS } from '@/types'
  import { sampleStockSchema } from '@/utils'
  import { useStore } from 'vuex'
  import Multiselect from '@/components/Select/multiselect.vue'
  import FormActions from '@/components/Forms/ModalActions/index.vue'
  import { StockAlias } from '@/services/api/models'
  import { useAcl } from 'vue-simple-acl/src'
  export default defineComponent({
    components: { Form, Field, Multiselect, Input, FormActions },
    props: {
      initForm: {
        type: Boolean,
        required: false,
        default: false
      },

      values: {
        type: Object as () => StockAlias,
        required: false,
        default: () => ({})
      }
    },
    emits: ['saveChanges', 'applyChanges'],
    setup(props, { emit }) {
      const acl = useAcl()
      const canDo = computed(
        () =>
          acl.role('admin') ||
          (acl.permission('is-manager') && acl.permission('write-stocks'))
      )
      const store = useStore()
      const initialDirty = ref({
        stockCategoryId: true,
        alias: true,
        inValuation: false,
        inNFD: false,
        inFD: false,
        inESOP: false
      })
      const newStock = ref<SampleStockTypeFormData>({
        stockCategoryId: props.values?.stockCategory?.id || '',
        alias: props.values?.alias || '',
        inValuation: props.values?.inValuation || false,
        inNFD: props.values?.inNFD || false,
        inFD: props.values?.inNFD || false,
        inESOP: props.values?.inESOP || false
      })

      const stockCategories = computed(
        () => store.getters['toolbox/stockCategories']
      )

      const mustInitForm = computed(
        () => store.getters['organizations/form'].initCurrentForm
      )

      watch(mustInitForm, (val) => {
        if (val) resetForm()
      })

      watch(props, (newProps) => {
        newStock.value.stockCategoryId = newProps.values?.stockCategory?.id
        newStock.value.alias = newProps.values?.alias
        newStock.value.inValuation = newProps.values?.inValuation || false
        newStock.value.inESOP = newProps.values?.inESOP || false
        newStock.value.inNFD = newProps.values?.inNFD || false
        newStock.value.inFD = newProps.values?.inFD || false
      })
      const schema = sampleStockSchema
      const handleForm = (formValues: SampleStockTypeFormData) => {
        emit(
          'saveChanges',
          formValues,
          STOCKTYPESALIAS.STOCK,
          props.values && props.values.id
        )
      }

      const categoryChanged = (id: string) => {
        newStock.value.stockCategoryId = id
        emit('applyChanges', newStock.value)
      }

      const resetForm = () => {
        newStock.value = {
          stockCategoryId: '',
          alias: '',
          inValuation: false,
          inNFD: false,
          inFD: false,
          inESOP: false
        }
      }

      onMounted(() => {
        resetForm()
      })

      watch(props, (newProps) => {
        if (newProps.initForm) {
          resetForm()
        } else {
          newStock.value.stockCategoryId =
            newProps.values?.stockCategory?.id || ''
          newStock.value.alias = newProps.values?.alias || ''
          newStock.value.inNFD = newProps.values?.inNFD || false
          newStock.value.inValuation = newProps.values?.inValuation || false
          newStock.value.inFD = newProps.values?.inFD || false
          newStock.value.inESOP = newProps.values?.inESOP || false
        }
      })
      return {
        schema,
        initialDirty,
        newStock,
        stockCategories,
        canDo,
        handleForm,
        categoryChanged
      }
    }
  })
</script>
<style lang="scss" scoped>
  #sample-stock {
    label {
      @apply uppercase;
    }
  }
</style>
